<template>
  <div class="mask-gallery">
    <!-- Page Header (only when viewed as standalone page, not in dialog) -->
    <header class="gallery-header" v-if="!inDialog">
      <h1 class="gallery-title">{{ $t('Word Cloud Shape Gallery') }}</h1>
      <p class="gallery-subtitle">
        {{ $t('Browse our collection of word cloud mask shapes. Hearts, stars, animals, letters, and more — find the perfect silhouette for your word art.') }}
      </p>
    </header>

    <!-- Search Box -->
    <div class="search-bar">
      <el-input
        v-model="keyword"
        @keyup.enter="searchMasks"
        :placeholder="$t('Search shapes — try heart, star, cat, love...')"
        size="large"
        clearable
        class="search-input"
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
    </div>

    <!-- Progress -->
    <el-progress v-if="loading" :percentage="progress" color="#6366f1" class="progress-bar" :stroke-width="4" />

    <!-- Mask Grid (visible to everyone) -->
    <div class="mask-grid" v-show="!loading && masks.length > 0">
      <!-- Standalone page: cards are links to /mask/:slug for SEO -->
      <template v-if="!inDialog">
        <router-link
          v-for="mask in masks"
          :key="mask.id"
          :to="`/mask/${getMaskSlug(mask)}`"
          class="mask-card"
          itemscope
          itemtype="https://schema.org/ImageObject"
        >
          <div class="mask-image-wrapper">
            <img
              :src="mask.pre_image_remote_url"
              :alt="getMaskAlt(mask)"
              :title="getMaskTitle(mask)"
              class="mask-image"
              loading="lazy"
              decoding="async"
              itemprop="contentUrl"
            />
            <div v-if="!isSubscribed" class="mask-premium-badge">
              <el-icon><Lock /></el-icon>
            </div>
          </div>
          <figcaption class="mask-caption" itemprop="name">
            {{ getMaskTitle(mask) }}
          </figcaption>
          <meta itemprop="description" :content="getMaskAlt(mask)" />
          <meta itemprop="thumbnailUrl" :content="mask.pre_image_remote_url" />
        </router-link>
      </template>

      <!-- Dialog mode: cards use click handler for selection -->
      <template v-if="inDialog">
        <figure
          v-for="mask in masks"
          :key="mask.id"
          class="mask-card"
          @click="handleMaskClick(mask)"
          itemscope
          itemtype="https://schema.org/ImageObject"
        >
          <div class="mask-image-wrapper">
            <img
            :src="mask.pre_image_remote_url"
            :alt="getMaskAlt(mask)"
            :title="getMaskTitle(mask)"
            class="mask-image"
            loading="lazy"
            decoding="async"
            itemprop="contentUrl"
          />
          <div v-if="!isSubscribed" class="mask-premium-badge">
            <el-icon><Lock /></el-icon>
          </div>
        </div>
        <figcaption class="mask-caption" itemprop="name">
          {{ getMaskTitle(mask) }}
        </figcaption>
        <meta itemprop="description" :content="getMaskAlt(mask)" />
        <meta itemprop="thumbnailUrl" :content="mask.pre_image_remote_url" />
      </figure>
      </template>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && masks.length === 0 && hasSearched" class="empty-state">
      <el-icon :size="48" class="empty-icon"><PictureFilled /></el-icon>
      <p>{{ $t('No shapes found. Try a different keyword like "heart", "star", or "flower".') }}</p>
    </div>

    <!-- Initial Loading Placeholder -->
    <div v-if="!loading && masks.length === 0 && !hasSearched" class="empty-state">
      <el-icon :size="48" class="empty-icon"><PictureFilled /></el-icon>
      <p>{{ $t('Enter a keyword above to browse shapes, or scroll through popular picks.') }}</p>
    </div>

    <!-- Pagination -->
    <div class="pagination-bar" v-if="totalPages > 1">
      <el-pagination
        background
        layout="prev, pager, next"
        :total="totalPages * pageSize"
        :page-size="pageSize"
        :current-page="currentPage"
        @current-change="onPageChange"
        :disabled="loading"
      />
    </div>

    <!-- JSON-LD Structured Data for Image Gallery -->
    <component :is="'script'" type="application/ld+json" v-if="masks.length > 0">
    {{ imageStructuredData }}
    </component>
  </div>
</template>

<script>
import axios from 'axios'
import { useSEO } from '@/composables/useSEO'
import { slugify } from '@/utils/slugify'
import { ElMessage } from 'element-plus'
import { Search, Lock, PictureFilled } from '@element-plus/icons-vue'

export default {
  name: 'SelectMask',
  components: {
    Search, Lock, PictureFilled
  },
  data() {
    return {
      masks: [],
      currentPage: 1,
      pageSize: 12, // Increased — better for image gallery browsing
      totalPages: 0,
      keyword: '',
      loading: false,
      progress: 0,
      isSubscribed: false,
      hasSearched: false,
    }
  },
  props: {
    inDialog: Boolean
  },
  computed: {
    imageStructuredData() {
      const items = this.masks.map((mask, index) => ({
        '@type': 'ImageObject',
        '@id': `#mask-${mask.id || index}`,
        name: this.getMaskTitle(mask),
        description: this.getMaskAlt(mask),
        contentUrl: mask.pre_image_remote_url,
        thumbnailUrl: mask.pre_image_remote_url,
        representativeOfPage: false
      }))
      return JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Word Cloud Mask Shapes',
        description: 'Browse word cloud shape masks for WordCloudStudio — hearts, stars, animals, letters, and more.',
        numberOfItems: items.length,
        itemListElement: items.map((item, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: item
        }))
      }, null, 2)
    }
  },
  methods: {
    /**
     * Generate a URL-safe slug from the mask name for linking to detail pages.
     */
    getMaskSlug(mask) {
      const name = mask.name || mask.title || ''
      return slugify(name)
    },

    /**
     * Build a descriptive alt text for the mask image.
     * Uses mask.name if available, otherwise extracts a readable name from the URL.
     */
    getMaskAlt(mask) {
      if (mask.name) return `Word cloud shape mask: ${mask.name}`
      if (mask.title) return `Word cloud shape mask: ${mask.title}`
      // Extract filename from URL as fallback
      const url = mask.pre_image_remote_url || ''
      const filename = url.split('/').pop().split('?')[0].split('.')[0]
      if (filename) {
        // Convert dashes/underscores to spaces and capitalize
        const readable = filename
          .replace(/[-_]/g, ' ')
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/\b\w/g, c => c.toUpperCase())
        return `Word cloud shape mask: ${readable}`
      }
      return 'Word cloud shape mask for custom word cloud design'
    },

    /**
     * Build a display title for the mask.
     */
    getMaskTitle(mask) {
      if (mask.name) return mask.name
      if (mask.title) return mask.title
      const url = mask.pre_image_remote_url || ''
      const filename = url.split('/').pop().split('?')[0].split('.')[0]
      if (filename) {
        return filename.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      }
      return `Shape #${mask.id || ''}`
    },

    hasPurchased() {
      const purchaseStatus = localStorage.getItem('purchaseStatus')
      const expirationTime = localStorage.getItem('expirationTime')
      const valid = purchaseStatus === 'purchased' && expirationTime && new Date() < new Date(expirationTime)
      this.isSubscribed = valid
      return valid
    },

    /**
     * Handle mask click:
     * - Subscribers: emit selection immediately
     * - Non-subscribers: show prompt to upgrade
     */
    handleMaskClick(mask) {
      if (this.isSubscribed) {
        // GA event: mask selected
        if (typeof window.gtag !== 'undefined') {
          window.gtag('event', 'mask_selected', {
            'mask_name': this.getMaskTitle(mask),
            'mask_source': 'gallery'
          })
        }
        this.$emit('mask-selected', mask.mask)
      } else {
        // GA event: mask preview click (non-subscriber)
        if (typeof window.gtag !== 'undefined') {
          window.gtag('event', 'mask_gallery_click', {
            'mask_name': this.getMaskTitle(mask),
            'action': 'premium_prompt'
          })
        }
        ElMessage({
          message: this.$t('Subscribe to use this shape in your word cloud. Choose a plan to unlock all masks.'),
          type: 'info',
          duration: 5000,
          showClose: true
        })
        // Emit event so parent can show pricing dialog
        this.$emit('show-pricing')
      }
    },

    generateToken() {
      const timestamp = Math.floor(Date.now() / 1000)
      const randomValue = crypto.randomUUID()
      const secretKey = '1abff3a1d9d11568ad55d4b458c6f7fe'

      const message = `${timestamp}.${randomValue}`
      const encoder = new TextEncoder()
      const messageData = encoder.encode(message)
      const keyData = encoder.encode(secretKey)

      return crypto.subtle.importKey(
        'raw', keyData,
        { name: 'HMAC', hash: 'SHA-256' },
        false, ['sign']
      ).then(key => {
        return crypto.subtle.sign('HMAC', key, messageData)
      }).then(signature => {
        const hashArray = Array.from(new Uint8Array(signature))
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
        return `${randomValue}.${timestamp}.${hashHex}`
      })
    },

    async fetchMasks() {
      try {
        this.loading = true
        this.hasSearched = true

        const progressInterval = setInterval(() => {
          this.progress = Math.min(this.progress + 10, 90)
        }, 500)

        const token = await this.generateToken()

        const response = await axios({
          method: 'post',
          url: '/wordcloudmaster/search',
          headers: { 'Content-Type': 'application/json' },
          data: {
            query: this.keyword,
            page: this.currentPage,
            page_size: this.pageSize,
            token: token
          }
        })

        clearInterval(progressInterval)
        this.progress = 100

        if (response.data?.data) {
          this.masks = response.data.data.masks || []
          this.totalPages = response.data.data.total_page || 0
        } else {
          this.masks = []
          this.totalPages = 0
        }

        // Track gallery page views (SEO-relevant)
        if (typeof window.gtag !== 'undefined' && this.masks.length > 0) {
          window.gtag('event', 'mask_gallery_view', {
            'keyword': this.keyword || '(browse)',
            'page': this.currentPage,
            'mask_count': this.masks.length
          })
        }

      } catch (error) {
        console.error('Error fetching masks:', error)
        this.masks = []
      } finally {
        this.loading = false
        setTimeout(() => { this.progress = 0 }, 200)
      }
    },

    searchMasks() {
      this.currentPage = 1
      this.fetchMasks()
    },

    onPageChange(page) {
      this.currentPage = page
      this.fetchMasks()
      // Scroll to top of gallery
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  },

  mounted() {
    // Open to search engines — this page is now a public image gallery.
    useSEO({
      title: 'Word Cloud Shape Gallery — Free Mask Templates | WordCloudStudio',
      description: 'Browse hundreds of word cloud mask shapes — hearts, stars, animals, letters, logos, and more. Find the perfect silhouette for your word art. Free to browse.',
      keywords: 'word cloud shapes, word cloud masks, word art templates, word cloud silhouette, wordcloud shape, text cloud shapes, tag cloud templates',
      canonical: '/mask',
      ogTitle: 'Word Cloud Shape Gallery — Free Mask Templates | WordCloudStudio',
      ogDescription: 'Browse hundreds of word cloud mask shapes. Hearts, stars, animals, letters, and more. Find the perfect silhouette for your word art.',
      ogImage: 'https://wordcloudstudio.com/wcdw.png',
      ogType: 'website',
      twitterTitle: 'Word Cloud Shape Gallery | WordCloudStudio',
      twitterDescription: 'Browse hundreds of word cloud mask shapes. Free to browse.',
      twitterImage: 'https://wordcloudstudio.com/wcdw.png'
    })

    this.hasPurchased()
    // Fetch popular masks immediately so the page has content for crawlers.
    this.fetchMasks()
  }
}
</script>

<style scoped>
/* ── Gallery Layout ─────────────────────────────── */
.mask-gallery {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 16px 48px;
}

.gallery-header {
  text-align: center;
  margin-bottom: 32px;
}

.gallery-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 8px;
}

.gallery-subtitle {
  font-size: 1rem;
  color: #64748b;
  max-width: 640px;
  margin: 0 auto;
  line-height: 1.6;
}

/* ── Search ─────────────────────────────────────── */
.search-bar {
  max-width: 560px;
  margin: 0 auto 28px;
}

.search-input :deep(.el-input__wrapper) {
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

/* ── Grid ───────────────────────────────────────── */
.mask-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.mask-card {
  margin: 0;
  cursor: pointer;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e2e8f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.mask-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px -8px rgba(0,0,0,0.12);
}

.mask-image-wrapper {
  position: relative;
  aspect-ratio: 1;
  overflow: hidden;
  background: #f8fafc;
}

.mask-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.mask-premium-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(99, 102, 241, 0.9);
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  backdrop-filter: blur(4px);
}

.mask-caption {
  padding: 10px 12px;
  font-size: 0.85rem;
  color: #475569;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
}

/* ── Empty State ────────────────────────────────── */
.empty-state {
  text-align: center;
  padding: 64px 20px;
  color: #94a3b8;
}

.empty-icon {
  display: block;
  margin: 0 auto 16px;
  color: #cbd5e1;
}

/* ── Progress & Pagination ──────────────────────── */
.progress-bar {
  max-width: 400px;
  margin: 0 auto 24px;
}

.pagination-bar {
  display: flex;
  justify-content: center;
  padding-top: 8px;
}
</style>
